import React, { useState, useEffect, useRef } from "react";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import android from "../../assets/qr/qr_code_cyber_android.png";
import ios from "../../assets/qr/qr_code_cyber_ios.webp";

interface QrModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialStore: "android" | "ios";
}

const QrModal: React.FC<QrModalProps> = ({ isOpen, onClose, initialStore }) => {
  const { t } = useTranslation();
  const [activeStore, setActiveStore] = useState<"android" | "ios">(initialStore);
  const [hasError, setHasError] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  // Escape tugmasi bosilganda modal yopiladi
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      firstButtonRef.current?.focus();
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleRetry = () => {
    setHasError(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="absolute inset-0" onClick={onClose}></div>
      <div
        ref={modalRef}
        className="relative bg-white rounded-xl shadow-xl p-8 w-[400px] max-w-[90vw] border border-gray-200 z-10"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-black transition-colors duration-200"
        >
          <X size={24} />
        </button>

        {/* Tugmalar */}
        <div className="flex gap-4 justify-center mb-6">
          <button
            ref={firstButtonRef}
            onClick={() => {
              setActiveStore("android");
              setHasError(false);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-base font-semibold ${
              activeStore === "android"
                ? "bg-brand-gradient hover:bg-brand-gradient transition text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            <FaGooglePlay size={16} />
            {t("googlePlay", "Google Play")}
          </button>
          <button
            onClick={() => {
              setActiveStore("ios");
              setHasError(false);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-base font-semibold ${
              activeStore === "ios"
                ? "bg-brand-gradient hover:bg-brand-gradient transition text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            <FaApple size={16} />
            {t("appStore", "App Store")}
          </button>
        </div>

        {/* QR Code */}
        <div className="flex flex-col items-center">
          {hasError ? (
            <div className="text-center">
              <p className="text-red-500 text-sm mb-4">
                {t("imageError", "Failed to load QR code. Please try again.")}
              </p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                {t("retry", "Retry")}
              </button>
            </div>
          ) : (
            <>
              <img
                src={activeStore === "android" ? android : ios}
                alt={`${activeStore} QR`}
                className="w-64 h-64 mx-auto border border-gray-300 rounded-md"
                onError={() => setHasError(true)}
                loading="lazy"
                decoding="async"
                // fetchpriority="low"
              />
              <p className="mt-4 text-sm text-gray-600 text-center">
                {t(
                  "qrInstruction",
                  `Scan to download from ${
                    activeStore === "android" ? "Google Play" : "App Store"
                  }`
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QrModal;
